'use client'

import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Image,
  Menu,
  Portal,
} from '@chakra-ui/react'
//import Link from 'next/link'
import { Link } from '@chakra-ui/react'
import { env } from 'next-runtime-env'
import { memo, useEffect, useMemo, useRef } from 'react'
import { FaBars, FaExternalLinkAlt } from 'react-icons/fa'
import type {
  IRCompanyConfig,
  IRThemeConfig,
  InvestorRelationsConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'

interface InvestorRelationsHeaderProps {
  onNavigateToSection?: (sectionId: string) => void
  activeSectionId: string | null
  useContextFunction: () => InvestorRelationsConfigContextType
  isPreview?: boolean
  organisationId?: string
  fundraisingEnabled?: boolean
}

// Memoized logo component to prevent re-renders when activeSectionId changes
const LogoImage = memo(
  ({ src }: { src: string }) => {
    return (
      <Image
        src={src}
        h="100%"
        w="100%"
        objectFit="contain"
        objectPosition={{ base: 'left center', md: 'center' }}
        loading="eager"
        fetchPriority="high"
      />
    )
  },
  (prevProps, nextProps) => prevProps.src === nextProps.src
)

const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')

const normaliseExternalUrl = (url?: string | null) => {
  const trimmedUrl = url?.trim()
  if (!trimmedUrl) return ''
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmedUrl)) return trimmedUrl
  if (trimmedUrl.startsWith('//')) return `https:${trimmedUrl}`
  return `https://${trimmedUrl}`
}

export function InvestorRelationsHeader({
  onNavigateToSection,
  activeSectionId,
  useContextFunction,
  isPreview = false,
  organisationId = undefined,
  fundraisingEnabled = false,
}: InvestorRelationsHeaderProps) {
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault()
    onNavigateToSection?.(sectionId)
  }

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType
  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig
  const primaryColor = themeConfig.primaryColor
  const fontFamily = themeConfig.fontStyle
  const firstSectionId = fundraisingEnabled
    ? 'fundraising-campaign'
    : 'corporate-presentation'
  const firstSectionLabel = fundraisingEnabled
    ? 'Fundraising Campaign'
    : 'Corporate presentation'

  const companyConfig = ((config?.companyConfig as IRCompanyConfig) ||
    {}) as IRCompanyConfig
  const companyName = companyConfig.companyName
  const companySiteURL = normaliseExternalUrl(companyConfig.companyWebsite)
  const companySlug = config?.companySlugPublished
  let companyLogoURL = companyConfig.logoUrl || ''
  if (isPreview && organisationId) {
    companyLogoURL = `/api/proxy/admin/investor-relations/${organisationId}/logo`
  } else if (!companyLogoURL && companySlug) {
    companyLogoURL = `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/logo`
  }

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={1000}
      bg="white"
      borderBottomWidth="1px"
      borderColor="gray.200"
      boxShadow="sm"
    >
      <Container maxW="7xl" py={{ base: 3, md: 4 }} px={{ base: 4, md: 6 }}>
        <Flex align="center" justify="space-between">
          {/* Organisation Logo */}
          <HStack gap={3}>
            <Box
              h={{ base: '48px', md: '64px' }}
              w={{ base: '160px', sm: '200px', md: '260px' }}
              display="flex"
              alignItems="center"
              justifyContent={{ base: 'flex-start', md: 'center' }}
              overflow="hidden"
              onClick={handleLogoClick}
              cursor="pointer"
            >
              {companyLogoURL && <LogoImage src={companyLogoURL} />}
            </Box>
          </HStack>

          {/* Desktop navigation */}
          <HStack
            as="nav"
            gap={{ lg: 3, xl: 4 }}
            fontSize={{ lg: 'sm', xl: 'md' }}
            color="gray.700"
            display={{ base: 'none', lg: 'flex' }}
            fontFamily={fontFamily}
            flex="1"
            justify="flex-end"
            minW={0}
          >
            <Link
              href={`#${firstSectionId}`}
              onClick={(e) => handleNavClick(e, firstSectionId)}
              color={primaryColor}
              whiteSpace="nowrap"
              backgroundColor="transparent"
              _hover={{
                textDecoration: 'underline',
              }}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              border="0"
              fontWeight={
                activeSectionId === firstSectionId ? 'semibold' : 'normal'
              }
              padding="6px 10px"
            >
              {firstSectionLabel}
            </Link>

            <Link
              href="#results"
              onClick={(e) => handleNavClick(e, 'results')}
              color={primaryColor}
              whiteSpace="nowrap"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              backgroundColor="transparent"
              _hover={{
                textDecoration: 'underline',
              }}
              border="0"
              fontWeight={activeSectionId === 'results' ? 'semibold' : 'normal'}
              padding="6px 10px"
            >
              Results
            </Link>

            <Link
              href="#market-data"
              onClick={(e) => handleNavClick(e, 'market-data')}
              color={primaryColor}
              whiteSpace="nowrap"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              backgroundColor="transparent"
              _hover={{
                textDecoration: 'underline',
              }}
              border="0"
              fontWeight={
                activeSectionId === 'market-data' ? 'semibold' : 'normal'
              }
              padding="6px 10px"
            >
              Market data
            </Link>

            <Link
              href="#regulatory-news"
              onClick={(e) => handleNavClick(e, 'regulatory-news')}
              color={primaryColor}
              whiteSpace="nowrap"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              backgroundColor="transparent"
              _hover={{
                textDecoration: 'underline',
              }}
              border="0"
              fontWeight={
                activeSectionId === 'regulatory-news' ? 'semibold' : 'normal'
              }
              padding="6px 10px"
            >
              Regulatory news
            </Link>

            <Link
              href="#documents"
              onClick={(e) => handleNavClick(e, 'documents')}
              color={primaryColor}
              whiteSpace="nowrap"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              backgroundColor="transparent"
              _hover={{
                textDecoration: 'underline',
              }}
              border="0"
              fontWeight={
                activeSectionId === 'documents' ? 'semibold' : 'normal'
              }
              padding="6px 10px"
            >
              Documents
            </Link>

            <Link
              href={companySiteURL || '#'}
              target="_blank"
              rel="noopener noreferrer"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              minH="32px"
              px={4}
              borderRadius="md"
              color="white"
              bg={primaryColor}
              fontFamily={fontFamily}
              lineHeight="1.2"
              textDecoration="none"
              whiteSpace="nowrap"
              borderWidth="0"
              boxShadow="none"
              outline="none"
              overflow="hidden"
              backgroundClip="padding-box"
              _before={{ display: 'none' }}
              _after={{ display: 'none' }}
              _hover={{
                textDecoration: 'none',
                opacity: 0.92,
              }}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              onClick={(event) => {
                if (!companySiteURL) {
                  event.preventDefault()
                }
              }}
            >
              Visit company site
            </Link>
          </HStack>

          {/* Mobile navigation */}
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                aria-label="Open navigation"
                variant="outline"
                size="sm"
                display={{ base: 'inline-flex', lg: 'none' }}
              >
                <FaBars />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    value={firstSectionId}
                    onClick={() => onNavigateToSection?.(firstSectionId)}
                  >
                    {firstSectionLabel}
                  </Menu.Item>
                  <Menu.Item
                    value="results"
                    onClick={() => onNavigateToSection?.('results')}
                  >
                    Results
                  </Menu.Item>
                  <Menu.Item
                    value="market-data"
                    onClick={() => onNavigateToSection?.('market-data')}
                  >
                    Market data
                  </Menu.Item>
                  <Menu.Item
                    value="regulatory-news"
                    onClick={() => onNavigateToSection?.('regulatory-news')}
                  >
                    Regulatory news
                  </Menu.Item>
                  <Menu.Item
                    value="documents"
                    onClick={() => onNavigateToSection?.('documents')}
                  >
                    Documents
                  </Menu.Item>
                  <Menu.Item asChild value="visit-site" color={primaryColor}>
                    <Link href={companySiteURL}>
                      Visit company site <FaExternalLinkAlt />
                    </Link>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Flex>
      </Container>
    </Box>
  )
}
